import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Image,
  Spacer,
  Divider,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ArrowBackIcon, CalendarIcon, TimeIcon } from "@chakra-ui/icons";
import { MdLocationOn, MdToday } from "react-icons/md";
import { useParams, useHistory } from "react-router-dom";
import axiosInstance from "../../helpers/axiosInstance";
import SettingsMenu from "../common/SettingsMenu";
import Translate from "../../components/LanguageTranslation/Translate";

const BackToTrips = () => {
  const { tripId } = useParams();
  const history = useHistory();
  const [journey, setJourney] = useState(null);
  const [tripDetails, setTripDetails] = useState(null);

  // Determine if the current screen is mobile (base) or larger.
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleBackToJourneys = () => {
    history.goBack();
  };

  useEffect(() => {
    const fetchTripById = async () => {
      try {
        const response = await axiosInstance.get(`/bucket-list/${tripId}`);
        const trip = response.data.data.trip_plan;
        const details = response.data.data.trip_details;
        if (trip) {
          setJourney(trip);
          setTripDetails(details);
        } else {
          // console.log("No trip found with the given ID");
        }
      } catch (error) {
        console.error("Error fetching trip by ID:", error);
      }
    };

    fetchTripById();
  }, [tripId]);

  if (!journey || !tripDetails) {
    return <Text>Loading...</Text>;
  }

  // Function to format the departure date into month (as text) and year
  const formatMonthYear = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();
    return { month, year };
  };

  const { month, year } = formatMonthYear(tripDetails.departure_date);

  // Define the individual info boxes
  const daysBox = (
    <Box display="flex" flexDirection="column" alignItems="center">
      <MdToday size={26} />
      <Text fontWeight="bold" mt={2} mb={0}>
        {tripDetails.days[0]}
      </Text>
      <Text fontSize="sm" mb={0}>
        <Translate text="Days" />
      </Text>
    </Box>
  );

  const monthYearBox = (
    <Box display="flex" flexDirection="column" alignItems="center">
      <CalendarIcon boxSize={6} />
      <Text fontWeight="bold" mt={2} mb={1}>
        {year}
      </Text>
      <Text fontSize="sm" mb={0}>
        {month}
      </Text>
    </Box>
  );

  const departureBox = (
    <Box display="flex" flexDirection="column" alignItems="center">
      <TimeIcon boxSize={6} />
      <Text fontWeight="bold" mt={2} mb={1}>
        {tripDetails.departure_date}
      </Text>
      <Text fontSize="sm" mb={0}>
        <Translate text="Departure Date" />
      </Text>
    </Box>
  );

  const destinationBox = (
    <Box display="flex" flexDirection="column" alignItems="center">
      <MdLocationOn size={24} />
      <Text fontWeight="bold" mt={2} mb={1}>
        {tripDetails.destination.join(", ")}
      </Text>
      <Text fontSize="sm" mb={0}>
        <Translate text="Destination" />
      </Text>
    </Box>
  );

  return (
    <Box w="full" h="100vh">
      <Box
        flex="1"
        display="flex"
        flexDirection="column"
        pb={1}
        h="8vh"
        borderBottom="1px"
        borderColor="blackAlpha.200"
      >
        <Box
          display="flex"
          alignItems="center"
          width="100%"
          height="100%"
          p={4}
          pt={2}
          pl={0}
          pb={2}
          boxSizing="border-box"
        >
          <Button
            leftIcon={<ArrowBackIcon />}
            onClick={handleBackToJourneys}
            mt={1}
            bg="transparent"
            _hover="none"
            _active="none"
          >
            <Translate text="Back" />
          </Button>
          <Spacer />
          <HStack spacing={{ base: 2, md: 4, lg: 4 }} mt={1}>
            <Button
              colorScheme="teal"
              size="sm"
              variant="outline"
              onClick={() => alert("Edit functionality coming soon!")}
            >
              <Translate text="Edit" />
            </Button>
            <Button
              colorScheme="red"
              size="sm"
              variant="outline"
              onClick={() => alert("Delete functionality coming soon!")}
            >
              <Translate text="Delete" />
            </Button>
            <SettingsMenu />
          </HStack>
        </Box>
      </Box>

      <Box p={3} pb={1} overflowY="auto" h="90vh" position="relative">
        {/* Image with overlay text */}
        <Box position="relative" h="70vh" w="100%" mb={0}>
          <Image
            src={
              journey.itinerary[0]?.activity_detail_list[0]
                ?.thumbnailHiResURL || "/placeholder-image.png"
            }
            alt={journey.trip_name}
            borderTopRadius="md"
            objectFit="cover"
            h="100%"
            w="100%"
            filter="blur(1px)"
          />
          <Box
            position="absolute"
            top="0"
            left="0"
            p={5}
            bg="rgba(0, 0, 0, 0.5)"
            color="white"
            borderTopRadius="md"
            maxW={{ base: "100%", md: "50%", lg: "50%" }}
            h="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="left"
            textAlign="left"
          >
            <Text
              fontSize={{ base: "xl", md: "2xl", lg: "2xl" }}
              fontWeight="bold"
              mb={2}
            >
              {journey.trip_name}
            </Text>
            <Text fontSize={{ base: "sm", md: "md", lg: "md" }}>
              {journey.description}
            </Text>
          </Box>
        </Box>

        {/* Other Details */}
        <VStack
          align="start"
          spacing={4}
          bg="#262B2F"
          p={4}
          pl={8}
          pr={8}
          h={{ base: "auto", md: "17vh", lg: "17vh" }}
          borderBottomRadius="md"
          color="white"
        >
          {isMobile ? (
            // For mobile, display two rows:
            <VStack spacing={4} w="full">
              <HStack w="full" justifyContent="space-between">
                {destinationBox}
                <Divider
                  orientation="vertical"
                  height="50px"
                  color="whiteAlpha.400"
                />
                {monthYearBox}
                <Divider
                  orientation="vertical"
                  height="50px"
                  color="whiteAlpha.400"
                />
                {daysBox}
              </HStack>
              <HStack w="full" justifyContent="space-between">
                {departureBox}
              </HStack>
            </VStack>
          ) : (
            // For larger screens, display a single row with all four boxes
            <HStack spacing={10} w="full" justifyContent="space-between">
              {daysBox}
              <Divider
                orientation="vertical"
                height="50px"
                color="whiteAlpha.400"
              />
              {monthYearBox}
              <Divider
                orientation="vertical"
                height="50px"
                color="whiteAlpha.400"
              />
              {departureBox}
              <Divider
                orientation="vertical"
                height="50px"
                color="whiteAlpha.400"
              />
              {destinationBox}
            </HStack>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default BackToTrips;
